import type { Money } from '../../api/threads'
import type { PriceComponent } from '../../api/objects'

/**
 * What a listing costs, arranged the way the domain arranges it.
 *
 * The order and the "required" marks are `internal/domain/listing/cost.go`,
 * mirrored here so that a breakdown a manager reads and a breakdown a draft
 * quotes are the same breakdown twice rather than two orderings of the same
 * rows.
 *
 * The distinction this whole module exists for: no row for the bills means
 * nobody has considered them, and a row whose amount is unknown means somebody
 * asked and was not told. The first is a gap in our work, the second is a fact
 * about the offer, and only the second is worth saying to a client.
 */

/** Paid every month, in the order the domain adds them. */
export const MONTHLY_ORDER = [
  'rent',
  'condominio',
  'fixed_bills',
  'variable_bills',
  'other_recurring',
] as const

/** Paid once, before or at the start. */
export const UPFRONT_ORDER = [
  'deposit',
  'first_month',
  'prepayment',
  'agency_fee',
  'romaready_fee',
  'one_time',
] as const

/**
 * The components whose absence makes a total unknown rather than smaller.
 *
 * A Rome tenancy always has a rent, building charges and bills, and §6.2 names
 * the deposit and the agency fee next to bills when it says unknown is not zero.
 * A flat that genuinely has no condominio is recorded as a known zero by
 * whoever established that; the difference between that row and no row at all is
 * the difference between a client quoted 800 and a client who pays 950 in
 * November.
 */
const REQUIRED = new Set<string>([
  'rent',
  'condominio',
  'fixed_bills',
  'variable_bills',
  'deposit',
  'agency_fee',
])

export type PriceRowState =
  /** Somebody named a figure — known or estimated. */
  | 'stated'
  /** Somebody asked and was not told. A fact about the offer. */
  | 'unknown'
  /** No row at all, for a component every offer has. A gap in our work. */
  | 'missing'

export interface PriceRow {
  readonly key: string
  readonly component: string
  readonly state: PriceRowState
  /** Undefined only when the row is missing: there is nothing to render, not even a kind. */
  readonly amount: Money | undefined
  readonly monthsHundredths: number | undefined
  readonly note: string
  readonly required: boolean
}

export interface PriceBreakdown {
  readonly monthly: readonly PriceRow[]
  readonly upfront: readonly PriceRow[]
  /** Required components with no row at all — nobody has asked yet. */
  readonly neverAsked: readonly string[]
  /** Components asked about whose amount the owner did not name. */
  readonly notStated: readonly string[]
  /**
   * Whether a monthly figure could be stated at all.
   *
   * False as soon as one required monthly part is unknown or missing, because a
   * total assembled from an unknown part is unknown and not the sum of the parts
   * we happen to know (invariant 2). The panel never adds the parts up itself in
   * either case: a total needs a scope — bills in or out, whole unit or per
   * person — and that is a decision, not arithmetic.
   */
  readonly monthlyCanBeStated: boolean
}

/** Every component this build knows where to put, whatever period it arrives with. */
const PLACED_BY_NAME = new Set<string>([...MONTHLY_ORDER, ...UPFRONT_ORDER])

/** Which of the two groups a period belongs to. Anything unrecognised is monthly. */
function groupOf(period: string): string {
  return period === 'once' ? 'once' : 'monthly'
}

function stateOf(amount: Money): PriceRowState {
  return amount.kind === 'unknown' || amount.amountMinor === null ? 'unknown' : 'stated'
}

function rowsFor(
  order: readonly string[],
  components: readonly PriceComponent[],
  period: string,
): PriceRow[] {
  const rows: PriceRow[] = []

  for (const name of order) {
    const found = components.filter((component) => component.component === name)

    if (found.length === 0) {
      // Only the required ones are named when absent. Listing every optional
      // component a landlord happens not to charge would bury the two lines that
      // mean somebody has work to do.
      if (REQUIRED.has(name)) {
        rows.push({
          key: name,
          component: name,
          state: 'missing',
          amount: undefined,
          monthsHundredths: undefined,
          note: '',
          required: true,
        })
      }
      continue
    }

    for (const component of found) {
      rows.push({
        key: component.id,
        component: component.component,
        state: stateOf(component.amount),
        amount: component.amount,
        monthsHundredths: component.monthsHundredths,
        note: component.note,
        required: REQUIRED.has(name),
      })
    }
  }

  // Anything the server sent that this build does not know the place of still
  // has to appear: a component missing from the screen is a charge the client
  // finds out about later. A period this build does not recognise lands in the
  // monthly group rather than nowhere, for the same reason.
  for (const component of components) {
    // Placed by name in whichever group its name belongs to, so a component
    // whose period contradicts its name is drawn once rather than twice.
    if (PLACED_BY_NAME.has(component.component)) continue
    if (groupOf(component.period) !== period) continue
    rows.push({
      key: component.id,
      component: component.component,
      state: stateOf(component.amount),
      amount: component.amount,
      monthsHundredths: component.monthsHundredths,
      note: component.note,
      required: false,
    })
  }

  return rows
}

export function breakdown(components: readonly PriceComponent[]): PriceBreakdown {
  const monthly = rowsFor(MONTHLY_ORDER, components, 'monthly')
  const upfront = rowsFor(UPFRONT_ORDER, components, 'once')
  const all = [...monthly, ...upfront]

  return {
    monthly,
    upfront,
    neverAsked: all.filter((row) => row.state === 'missing').map((row) => row.component),
    notStated: all.filter((row) => row.state === 'unknown').map((row) => row.component),
    monthlyCanBeStated: !monthly.some((row) => row.required && row.state !== 'stated'),
  }
}
